/**
 * D10 dunning notice: a personal subscription is `past_due` and the server
 * still grants access while Stripe retries the charge. Tells the user their
 * last payment failed and opens the Stripe billing portal (the same
 * `billingAPI.portal()` action Profile and Billing Recovery use).
 *
 * Dismissal is per session: in-memory only, so it comes back on the next
 * app load while the subscription is still past_due. Same strip layout as
 * PasswordlessMigrationBanner.
 */
import React, { useEffect, useState } from 'react';
import { Linking, Platform, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import { billingAPI } from '@/services/api';
import { trackAuthEvent } from '@/services/analytics';
import { shouldShowPaymentFailedBanner } from '@/utils/billingAccess';

const COLORS = {
  bg: '#FFFBEB', // light amber
  border: '#FDE68A',
  iconBg: '#B45309',
  text: '#1E293B',
  textMuted: '#475569',
  error: '#B91C1C',
  primary: '#B45309',
};

export default function PaymentFailedBanner() {
  const { t } = useTranslation();
  const { user, billingStatus } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDismissed(false);
    setError(null);
  }, [user?.id]);

  if (!user || dismissed || !shouldShowPaymentFailedBanner(billingStatus)) return null;

  const openBillingPortal = async () => {
    if (opening) return;
    setOpening(true);
    setError(null);
    // Open the tab inside the click handler so popup blockers allow it.
    let popup: Window | null = null;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      popup = window.open('about:blank', '_blank');
    }
    try {
      const { url } = await billingAPI.portal();
      trackAuthEvent('billing_portal_opened', { platform: Platform.OS, surface: 'payment_failed_banner' });
      if (popup) popup.location.href = url;
      else await Linking.openURL(url);
    } catch (e: any) {
      popup?.close();
      const status = e?.response?.status;
      trackAuthEvent('billing_portal_failed', {
        error_type: status ? `http_${status}` : 'exception',
        surface: 'payment_failed_banner',
      });
      setError(t('paymentFailedBanner.portalError'));
    } finally {
      setOpening(false);
    }
  };

  return (
    <View
      testID="payment_failed_banner"
      accessibilityRole="alert"
      style={{
        backgroundColor: COLORS.bg,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
        paddingHorizontal: 20,
        paddingVertical: 12,
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        ...(Platform.OS === 'web'
          ? ({ position: 'sticky' as any, top: 0, zIndex: 40 } as any)
          : {}),
      }}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: COLORS.iconBg,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <MaterialIcons name="credit-card-off" size={18} color="white" />
      </View>

      <View style={{ flex: 1, flexShrink: 1, minWidth: 200 }}>
        <Text style={{ color: COLORS.text, fontWeight: '600', fontSize: 14, marginBottom: 2 }}>
          {t('paymentFailedBanner.title')}
        </Text>
        <Text style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 18 }}>
          {t('paymentFailedBanner.body')}
        </Text>
        {error ? (
          <Text style={{ color: COLORS.error, fontSize: 13, marginTop: 4 }}>{error}</Text>
        ) : null}
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <TouchableOpacity
          testID="payment_failed_banner_update"
          onPress={openBillingPortal}
          disabled={opening}
          accessibilityRole="button"
          accessibilityLabel={t('paymentFailedBanner.cta')}
          style={{
            paddingHorizontal: 14,
            paddingVertical: 8,
            borderRadius: 8,
            backgroundColor: COLORS.primary,
            opacity: opening ? 0.6 : 1,
          }}
        >
          <Text style={{ color: 'white', fontWeight: '600', fontSize: 13 }}>
            {t('paymentFailedBanner.cta')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          testID="payment_failed_banner_dismiss"
          onPress={() => setDismissed(true)}
          accessibilityRole="button"
          accessibilityLabel={t('paymentFailedBanner.dismiss')}
          style={{ padding: 6 }}
        >
          <MaterialIcons name="close" size={18} color={COLORS.textMuted} />
        </TouchableOpacity>
      </View>
    </View>
  );
}
